import * as React from 'react';
import { createPortal } from 'react-dom';

/**
 * Coordinates a set of docked {@link MultiSelect} panels so at most one is open
 * at a time, and owns the slot they render into.
 *
 * The dropdown variant gets the "one at a time" part for free — Radix's popover
 * closes on an outside pointer-down, and a click on a sibling trigger *is*
 * outside. The docked panel is a plain absolutely-positioned card with no such
 * dismiss layer, so without a shared owner two panels stack on top of each
 * other and the modal reads as broken.
 *
 * The group also renders the DOCK ITSELF and hands panels a portal target.
 * A docked panel is positioned against the modal card, not against its field,
 * but the field lives several levels down inside the form — so leaving the
 * panel where it is written makes its geometry hostage to every wrapper in
 * between. Two of those wrappers had already broken it: a `relative` on the
 * field group stole the positioning ancestor, and the form's `overflow-y-auto`
 * clipped the panel away entirely (`overflow-x` cannot stay `visible` when the
 * other axis scrolls). Portalling into a slot the group controls makes the
 * anchor explicit and immune to both.
 *
 * A `MultiSelect` used outside a provider falls back to its own local state and
 * renders in place, so the component still works standalone (and in isolation
 * tests).
 */
export interface MultiSelectGroupValue {
  /** Id of the panel currently open, or `null` when the dock is empty. */
  openId: string | null;
  /** Open `id`, replacing whatever held the dock; `null` closes it. */
  setOpenId: (id: string | null) => void;
  /** Slot the open panel portals into; `null` until the dock mounts. */
  dock: HTMLElement | null;
}

const MultiSelectGroupContext = React.createContext<MultiSelectGroupValue | null>(null);

/**
 * Wrap the fields that share one dock (e.g. a modal's form). Panels inside
 * close each other; panels outside are unaffected.
 *
 * Renders a zero-size positioned slot as its LAST child. The slot must be a
 * sibling of the scrolling form rather than a descendant, so put this provider
 * directly inside the modal card — the dock then measures against that card,
 * which is what the design's «panel beside the modal» geometry assumes.
 */
export function MultiSelectGroup({ children }: { children: React.ReactNode }) {
  const [openId, setOpenId] = React.useState<string | null>(null);
  // `useState` rather than `useRef`: mounting the slot must re-render the
  // consumers, otherwise the first panel to open portals into `null` and never
  // appears until some unrelated render happens to flush it.
  const [dock, setDock] = React.useState<HTMLElement | null>(null);

  const value = React.useMemo<MultiSelectGroupValue>(
    () => ({ openId, setOpenId, dock }),
    [openId, dock],
  );
  return (
    <MultiSelectGroupContext.Provider value={value}>
      {children}
      {/* The dock: an inert overlay pinned to the modal card, which panels
       * position themselves against. `absolute` takes it out of flow, so it
       * adds no row to the card's grid and no box to its flex column;
       * `pointer-events-none` keeps it from swallowing clicks on the form
       * beneath (the open panel re-enables pointer events on itself).
       *
       * Deliberately NOT `aria-hidden`: the slot is empty, but the panel that
       * portals into it is the real options list, and `aria-hidden` on an
       * ancestor drops that whole subtree out of the accessibility tree. */}
      <div ref={setDock} className="pointer-events-none absolute inset-0" />
    </MultiSelectGroupContext.Provider>
  );
}

/** Read the enclosing group, or `null` when there is none. */
export function useMultiSelectGroup(): MultiSelectGroupValue | null {
  return React.useContext(MultiSelectGroupContext);
}

/**
 * Render `children` into the enclosing group's dock, or in place when there is
 * no group (standalone use, isolation tests).
 */
export function MultiSelectDock({ children }: { children: React.ReactNode }) {
  const group = useMultiSelectGroup();
  if (!group) return <>{children}</>;
  if (!group.dock) return null;
  return createPortal(children, group.dock);
}

/**
 * The open/close state for one panel: group-owned when inside a
 * {@link MultiSelectGroup}, component-local otherwise. Both branches run every
 * render — only which result is returned changes — so the hook order is stable.
 */
export function usePanelDisclosure(id: string): {
  open: boolean;
  setOpen: (next: boolean) => void;
} {
  const group = useMultiSelectGroup();
  const [localOpen, setLocalOpen] = React.useState(false);

  const groupSetOpenId = group?.setOpenId;
  const setGroupOpen = React.useCallback(
    (next: boolean) => groupSetOpenId?.(next ? id : null),
    [groupSetOpenId, id],
  );

  return group
    ? { open: group.openId === id, setOpen: setGroupOpen }
    : { open: localOpen, setOpen: setLocalOpen };
}
